/**
 * Service for handling compliance API requests
 */

import { fetchBaseUrl, getOCSFSeverityNameFromNumber } from "core/utils/common";
import { t } from "i18next";


const API_BASE_URL = `${fetchBaseUrl({port: 8002})}/ac2/ui/compliance`;

/**
 * Generic API request function to handle all API calls
 * @param endpoint API endpoint path
 * @param method HTTP method
 * @param headers Additional headers
 * @param body Request body (for POST/PUT requests)
 * @returns Promise with response data
 */
const apiRequest = async (
  endpoint: string, 
  method: string = 'GET', 
  headers: Record<string, string> = {}, 
  token: string,
  body?: any
): Promise<any> => {
  try {
    const requestOptions: RequestInit = {
      method,
      headers: {
        'X-API-KEY': token,
        'Content-Type': 'application/json',
        ...headers
      }
    };

    if (body && (method === 'POST' || method === 'PUT')) {
      requestOptions.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error in API request to ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Fetches framework controls from the API
 * @param framework Framework identifier
 * @returns Promise with framework controls data
 */
export const getFrameworkControls = async (framework: string, token: string): Promise<any> => {
  return apiRequest(`/frameworks/${framework}/controls`, "GET", {}, token);
};

/**
 * Fetches framework rules from the API
 * @param framework Framework identifier
 * @returns Promise with framework rules data
 */
export const getFrameworkRules = async (framework: string, token: string): Promise<any> => {
  return apiRequest(`/frameworks/${framework}/rules`, "GET", {}, token);
};

/**
 * Fetches both framework controls and rules in a single method call
 * @param framework Framework identifier
 * @returns Promise with an object containing both controls and rules
 */
export const getFrameworkData = async (framework: string, token: string): Promise<{controls: any, rules: any}> => {
  try {
    const [controls, rules] = await Promise.all([
      getFrameworkControls(framework, token),
      getFrameworkRules(framework, token)
    ]);
    
    return { controls, rules };
  } catch (error) {
    console.error('Error fetching framework data:', error);
    throw error;
  }
};

/**
 * Maps API data to the format expected by the ComplianceTable component
 * @param controls Framework controls data
 * @param rules Framework rules data
 * @returns Formatted data for the table component
 */
export const mapFrameworkData = (controls: any, rules: any, framework: string = 'DORA') => {
  try {
    const mappedData = {
      framework: framework,
      data: []
    };
    
    // Map controls and their associated rules based on the actual API response format
    if (Array.isArray(controls?.data)) {
      mappedData.data = controls.data.map((control: any, index: number) => {
        // Find related rules for this control
        const controlRules = Array.isArray(rules?.data) 
          ? rules.data.filter((rule: any) => rule.control_id === control.control_id)
          : [];

        console.log(`controlRules: ${JSON.stringify(controlRules)}`);
          
        return {
          id: index + 1,
          title: control.control_id + " " + control.control_description,
          description: control.control_description,
          passed: control.pass_count + control.fail_count > 0 
            ? `${control.pass_count} of ${control.total_count}`
            : "0 of 0",
          compliance: control.compliance_percentage !== undefined 
            ? `${Math.round(control.compliance_percentage)}%` 
            : "0%",
          findings: controlRules.map((rule: any) => ({
            description: formatRuleName(rule.rule_name),
            guidance: rule.guidance,
            rawRuleName: rule.rule_name,
            severity: getOCSFSeverityNameFromNumber(t, 0),
            type: determineType(rule.rule_name),
            provider: rule.provider || "AWS",
            compliance: rule.compliance_percentage !== undefined 
              ? `${Math.round(rule.compliance_percentage)}%` 
              : "0%",
            passed: rule.pass_count + rule.fail_count > 0 
              ? `${rule.pass_count} of ${rule.total_count}` 
              : "0 of 0",
          }))
        };
      });
    }
    
    return mappedData;
  } catch (error) {
    console.error('Error mapping framework data:', error);
    throw error;
  }
};

// Helper function to determine type based on rule name
const determineType = (ruleName: string): string => {
  if (!ruleName) return 'Unknown';
  
  if (ruleName.includes('s3') || ruleName.includes('bucket')) return 'Bucket';
  if (ruleName.includes('ec2') || ruleName.includes('instance')) return 'Compute';
  if (ruleName.includes('iam') || ruleName.includes('access')) return 'IAM';
  if (ruleName.includes('rds') || ruleName.includes('database')) return 'Database';
  if (ruleName.includes('log') || ruleName.includes('trail')) return 'Logging';
  if (ruleName.includes('vpc') || ruleName.includes('network')) return 'Network';
  if (ruleName.includes('backup')) return 'Backup';
  if (ruleName.includes('key') || ruleName.includes('encrypt')) return 'Encryption';
  
  return 'General';
};


// Helper function to format rule names
const formatRuleName = (ruleName: string): string => {
  if (!ruleName) return '';
  
  // Replace hyphens and underscores with spaces
  let formattedName = ruleName.replace(/[-_]/g, ' ');
  
  // Capitalize each word
  formattedName = formattedName.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return formattedName;
};